import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { brand } from '@/shared/config/brand';
import { legalConfig } from '@/shared/config/legal';
import { LegalPageLayout, LegalSection } from '../components/LegalPageLayout';

function ContactEmail() {
  return (
    <a href={`mailto:${legalConfig.supportEmail}`}>{legalConfig.supportEmail}</a>
  );
}

/** Product brand name used in legal / A2P copy (must match SMS sender branding). */
const productName = brand.uiName;

export function TermsPage() {
  return (
    <LegalPageLayout
      title={`${productName} Terms & Conditions`}
      metaTitle={`${productName} Terms & Conditions`}
      metaDescription={`Review the terms and conditions governing the use of ${productName} services, accounts, authentication, and communications.`}
    >
      <Typography component="p">
        These Terms &amp; Conditions (&quot;Terms&quot;) govern your access to and use of{' '}
        {productName} (&quot;{productName},&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
        website at <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a> and related services
        (the &quot;Services&quot;). {productName} is operated by {legalConfig.legalEntityName}.
      </Typography>
      <Typography component="p">
        By accessing or using the Services, you agree to these Terms. If you do not agree, do not use
        the Services.
      </Typography>
      <Typography component="p">
        Operated by: {legalConfig.legalEntityName}
        <br />
        Business address: {legalConfig.businessAddress}
        <br />
        Contact: <ContactEmail />
      </Typography>

      <LegalSection id="acceptance" title="1. Acceptance of Terms">
        <Typography component="p">
          You accept these Terms by creating an account, signing in, requesting an OTP, submitting a
          guide application, making a booking or connection request, or otherwise using the Services.
        </Typography>
      </LegalSection>

      <LegalSection id="eligibility" title="2. Eligibility">
        <Typography component="p">
          You must be legally able to enter into a binding agreement and use the Services in your
          jurisdiction. Guide applicants must provide accurate verification information as requested
          during onboarding.
        </Typography>
      </LegalSection>

      <LegalSection id="registration" title="3. Account Registration">
        <Typography component="p">
          You may create or access an account using mobile phone OTP authentication and/or Google
          Sign-In. You agree to provide accurate information and to keep your account details up to
          date. You are responsible for activity under your account.
        </Typography>
      </LegalSection>

      <LegalSection id="responsibilities" title="4. User Responsibilities">
        <Typography component="p">You agree to:</Typography>
        <ul>
          <li>Use the Services only for lawful purposes</li>
          <li>Provide truthful profile, booking, and verification information</li>
          <li>Respect other users, including travelers and guides</li>
          <li>Keep authentication codes and account access confidential</li>
          <li>Comply with applicable laws and these Terms</li>
        </ul>
      </LegalSection>

      <LegalSection id="security" title="5. Account Security">
        <Typography component="p">
          You are responsible for safeguarding access to your devices and accounts used with{' '}
          {productName}. Notify us promptly if you believe your account or phone number is being used
          without authorization. Never share one-time verification codes with anyone.
        </Typography>
      </LegalSection>

      <LegalSection id="services" title="6. Services">
        <Typography component="p">
          {productName} provides a marketplace platform that helps travelers discover and connect with
          local guides, manage requests and bookings, and access related features such as reviews,
          notifications, and (where enabled) payments. {productName} does not itself provide the
          underlying guided experiences offered by independent guides unless expressly stated
          otherwise.
        </Typography>
      </LegalSection>

      <LegalSection id="user-content" title="7. User Content and Information">
        <Typography component="p">
          You retain ownership of content you submit (such as profile text, photos you own, and
          reviews), and you grant {productName} a non-exclusive license to host, display, and use that
          content as needed to operate and promote the Services. You represent that you have the
          rights needed to submit such content.
        </Typography>
      </LegalSection>

      <LegalSection id="acceptable-use" title="8. Acceptable Use">
        <Typography component="p">You may not:</Typography>
        <ul>
          <li>Misrepresent your identity or affiliations</li>
          <li>Attempt to bypass authentication, payments, or security controls</li>
          <li>Harass, defraud, or harm other users</li>
          <li>Upload unlawful, infringing, or harmful content</li>
          <li>Scrape, overload, or reverse engineer the Services except as allowed by law</li>
          <li>Use the Services for spam or unauthorized commercial messaging</li>
        </ul>
      </LegalSection>

      <LegalSection id="third-party" title="9. Third-Party Services">
        <Typography component="p">
          The Services integrate with third-party providers, including Google (Sign-In and
          Analytics), Twilio (authentication SMS), Cloudinary (media storage), and Cashfree
          (payments), when those features are enabled. Your use of those providers may also be
          subject to their own terms and privacy policies. {productName} is not responsible for
          third-party services outside our reasonable control.
        </Typography>
      </LegalSection>

      <LegalSection id="communications" title="10. Communications">
        <Typography component="p">
          We may send service-related communications by email or in-app notification, including
          booking updates and account notices. These non-SMS channels are separate from the SMS
          authentication campaign described in Section 11.
        </Typography>
        <Typography component="p">
          SMS messages under our Twilio A2P 10DLC 2FA / OTP campaign are limited to transactional
          authentication and security verification codes, as described below. That SMS channel is not
          used for booking updates, promotional messages, marketing messages, or general notification
          SMS.
        </Typography>
      </LegalSection>

      <LegalSection id="sms-otp" title="11. SMS and OTP Communications">
        <Typography component="p">
          You may authenticate using mobile phone OTP and/or Google Sign-In. {productName} may send
          SMS messages containing one-time verification codes when you initiate authentication or
          security verification. Examples include:
        </Typography>
        <ul>
          <li>Account registration</li>
          <li>Login verification</li>
          <li>Phone number verification</li>
          <li>Account / security verification</li>
          <li>Other user-initiated identity verification steps supported by the Services</li>
        </ul>
        <Typography component="p">
          These SMS messages are transactional authentication / security messages. They are not
          marketing or promotional messages. Message frequency varies based on user-initiated
          authentication activity. Message and data rates may apply.
        </Typography>
        <Typography component="p">
          You must provide explicit SMS consent through the {productName} phone authentication flow
          before an OTP SMS is sent. Consent is given by selecting an SMS consent checkbox that is
          unchecked by default (for example at{' '}
          <a href={`${legalConfig.websiteUrl}/auth/phone?intent=guide`}>
            {legalConfig.websiteUrl}/auth/phone?intent=guide
          </a>
          ). This SMS consent is separate from acceptance of these Terms &amp; Conditions and the
          Privacy Policy. There is no SMS keyword opt-in (such as texting START, YES, or UNSTOP).
        </Typography>
        <Typography component="p">
          OTPs expire after the configured period (currently {legalConfig.otpExpiryMinutes} minutes
          by default). Never share verification codes with anyone.
        </Typography>
        <Typography component="p">
          You may reply STOP to opt out of further SMS messages and HELP for help. For SMS or account
          security concerns, contact <ContactEmail />.
        </Typography>
      </LegalSection>

      <LegalSection id="ip" title="12. Intellectual Property">
        <Typography component="p">
          The {productName} name, logos, website design, and platform content (excluding user-submitted
          content) are owned by {productName} or its licensors and are protected by applicable
          intellectual property laws. You may not use our branding without prior written permission.
        </Typography>
      </LegalSection>

      <LegalSection id="privacy" title="13. Privacy">
        <Typography component="p">
          Our collection and use of personal information is described in our{' '}
          <RouterLink to={legalConfig.privacyPolicyPath}>Privacy Policy</RouterLink>. By using the
          Services, you acknowledge that policy.
        </Typography>
      </LegalSection>

      <LegalSection id="disclaimers" title="14. Disclaimers">
        <Typography component="p">
          The Services are provided on an &quot;as is&quot; and &quot;as available&quot; basis to the
          fullest extent permitted by law. We do not warrant that the Services will be uninterrupted,
          error-free, or completely secure. Guide experiences are provided by independent guides;
          availability, quality, and outcomes may vary.
        </Typography>
      </LegalSection>

      <LegalSection id="liability" title="15. Limitation of Liability">
        <Typography component="p">
          To the fullest extent permitted by law, {productName} and its operators will not be liable
          for indirect, incidental, special, consequential, or punitive damages, or for loss of
          profits, data, or goodwill, arising from your use of the Services. Our aggregate liability
          for claims relating to the Services is limited to the amounts you paid to {productName} for
          the Services giving rise to the claim during the twelve (12) months before the claim, or a
          greater amount if required by applicable law.
        </Typography>
      </LegalSection>

      <LegalSection id="indemnification" title="16. Indemnification">
        <Typography component="p">
          You agree to defend, indemnify, and hold harmless {productName} and its operators from
          claims, damages, losses, and expenses (including reasonable legal fees) arising out of your
          misuse of the Services, your content, or your violation of these Terms or applicable law.
        </Typography>
      </LegalSection>

      <LegalSection id="suspension" title="17. Suspension and Termination">
        <Typography component="p">
          We may suspend or terminate access to the Services if we reasonably believe you violated
          these Terms, pose a security or fraud risk, or if required for legal or operational
          reasons. You may stop using the Services at any time. Provisions that by their nature
          should survive termination will survive.
        </Typography>
      </LegalSection>

      <LegalSection id="changes" title="18. Changes to Terms">
        <Typography component="p">
          We may update these Terms from time to time. The effective date above will change when
          updates are posted. Continued use of the Services after changes become effective constitutes
          acceptance of the updated Terms.
        </Typography>
      </LegalSection>

      <LegalSection id="governing-law" title="19. Governing Law">
        <Typography component="p">
          These Terms are governed by the laws of India. Subject to applicable consumer protection
          laws, courts in Gurugram, Haryana shall have jurisdiction over disputes arising from these
          Terms, without prejudice to any mandatory rights you may have in your place of residence.
        </Typography>
      </LegalSection>

      <LegalSection id="contact" title="20. Contact Information">
        <Typography component="p">
          Questions about these Terms may be sent to:
        </Typography>
        <Typography component="p">
          {productName}
          <br />
          Operated by: {legalConfig.legalEntityName}
          <br />
          Website:{' '}
          <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a>
          <br />
          Email: <ContactEmail />
          <br />
          Address: {legalConfig.businessAddress}
        </Typography>
        <Typography component="p">
          Related documents:{' '}
          <RouterLink to={legalConfig.privacyPolicyPath}>Privacy Policy</RouterLink>
        </Typography>
      </LegalSection>
    </LegalPageLayout>
  );
}
